import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface Suggestion {
  query: string;
  category: string;
  description: string;
  risk: "low" | "medium" | "high";
  examples: string[];
}

interface UseDorkSuggestionsResult {
  suggestions: Suggestion[];
  isLoading: boolean;
  error: string | null;
  needsAuth: boolean;
  retry: () => void;
}

// Session-scoped client cache
const sessionCache = new Map<string, Suggestion[]>();

// In-flight request deduplication
const inflightRequests = new Map<string, Promise<Suggestion[]>>();

const DEBOUNCE_MS = 300;
const MIN_LENGTH = 2;

export function useDorkSuggestions(keyword: string): UseDorkSuggestionsResult {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const retryCountRef = useRef(0);

  const fetchSuggestions = useCallback(async (kw: string, signal: AbortSignal) => {
    const cacheKey = kw.toLowerCase().trim();

    if (sessionCache.has(cacheKey)) {
      setSuggestions(sessionCache.get(cacheKey)!);
      setIsLoading(false);
      setError(null);
      setNeedsAuth(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setNeedsAuth(false);

    // Dedup: reuse in-flight request for same keyword
    if (inflightRequests.has(cacheKey)) {
      try {
        const result = await inflightRequests.get(cacheKey)!;
        if (!signal.aborted) {
          setSuggestions(result);
          setError(null);
        }
      } catch (err: unknown) {
        if (signal.aborted) return;
        const msg = err instanceof Error ? err.message : "Failed to load suggestions";
        setError(msg);
      } finally {
        if (!signal.aborted) setIsLoading(false);
      }
      return;
    }

    const fetchPromise = (async (): Promise<Suggestion[]> => {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/suggest-dorks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ keyword: kw }),
        signal,
      });

      if (response.status === 401) {
        setNeedsAuth(true);
        return [];
      }

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          throw new Error("Rate limit reached — try again in a moment.");
        }
        throw new Error(errData?.error ?? `Request failed (${response.status})`);
      }

      const data = await response.json();
      if (!data.ok) {
        throw new Error(data.error ?? "Failed to load suggestions");
      }

      return data.suggestions ?? [];
    })();

    inflightRequests.set(cacheKey, fetchPromise);

    try {
      const result = await fetchPromise;
      sessionCache.set(cacheKey, result);
      if (!signal.aborted) {
        setSuggestions(result);
        setError(null);
      }
    } catch (err: unknown) {
      if (signal.aborted) return;
      const msg = err instanceof Error ? err.message : "Failed to load suggestions";
      setError(msg);
    } finally {
      inflightRequests.delete(cacheKey);
      if (!signal.aborted) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    abortRef.current?.abort();

    if (keyword.length < MIN_LENGTH) {
      setSuggestions([]);
      setIsLoading(false);
      setError(null);
      setNeedsAuth(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const timeout = setTimeout(() => {
      fetchSuggestions(keyword.trim(), controller.signal);
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [keyword, fetchSuggestions]);

  const retry = useCallback(() => {
    retryCountRef.current += 1;
    abortRef.current?.abort();
    if (keyword.length < MIN_LENGTH) return;

    sessionCache.delete(keyword.toLowerCase().trim());

    const controller = new AbortController();
    abortRef.current = controller;
    fetchSuggestions(keyword.trim(), controller.signal);
  }, [keyword, fetchSuggestions]);

  return { suggestions, isLoading, error, needsAuth, retry };
}
